import { ArrowRight, FolderGit2, GitBranchPlus, MonitorPlay, Play, SquareTerminal } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { renderTemplate, safeBranch, templatePrompt, templateValuesError } from '@orgus/protocol';
import type { MissionTemplate } from '@orgus/protocol';
import { Button, ErrorState, Input, Pressable, Segmented, Select, Skeleton, StatusDot, Textarea, cn, shortPath, toast, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useOrgus, useMachines, useProjects } from '../../../lib/store.ts';
import { missionTone, missionsSlice, viewOf } from '../../missions/model.ts';
import { loadTemplates, startFromTemplate, useOnboarding } from '../client.ts';
import type { StartResult } from '../client.ts';
import { defaultTarget, describeTemplate, firstMissionLine, orderTemplates, thisMachine } from '../model.ts';
import { Callout } from '../parts.tsx';
import { TemplateIcon } from '../TemplateIcon.tsx';
import { CUSTOM_TASK } from '../customTask.ts';

function TemplateCard({ t, selected, onSelect }: { t: MissionTemplate; selected: boolean; onSelect: () => void }) {
  return (
    <Pressable
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-start gap-3 rounded-card border bg-surface p-3 text-left transition-colors hover:border-border-strong hover:bg-hover',
        selected ? 'border-accent ring-1 ring-accent' : 'border-border',
      )}
    >
      <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-control ring-1', selected ? 'bg-accent-soft text-accent ring-accent-line' : 'bg-raised text-fg-subtle ring-border')} aria-hidden>
        <TemplateIcon icon={t.icon} className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-ui font-medium text-fg">{t.title}</span>
          {!t.builtIn && <span className="shrink-0 rounded bg-raised px-2 text-caption text-fg-subtle ring-1 ring-border-subtle">This hub</span>}
        </span>
        <span className="mt-1 line-clamp-2 block text-caption text-fg-muted">{describeTemplate(t)}</span>
      </span>
    </Pressable>
  );
}

/** The started mission, live: starting on the machine until its session reports, then running there. */
function MissionLive({ missionId, machineId }: { missionId: string; machineId: string }) {
  const ext = useOrgus((s) => s.ext);
  const sessions = useOrgus((s) => s.sessions);
  const machines = useOrgus((s) => s.machines);
  const mission = missionsSlice(ext)?.missions.find((m) => m.id === missionId) ?? null;
  const view = mission ? viewOf(mission, { ext, sessions, machines }, Date.now()) : null;
  const line = firstMissionLine(view, machines[machineId]?.name ?? 'the machine');
  if (!view || !line) return null;
  return (
    <span className="mt-2 flex items-center gap-2 text-caption text-fg" role="status">
      <StatusDot tone={missionTone(view.state)} />
      {line}
    </span>
  );
}

/** Step 4: pick a template, fill in a field or two, choose where it runs, start. */
export function MissionStep({ onConnectRepo }: { onConnectRepo: () => void }) {
  const navigate = useNavigate();
  const phase = useOnboarding((s) => s.templatesPhase);
  const loadError = useOnboarding((s) => s.templatesError);
  const raw = useOnboarding((s) => s.templates);
  const reach = useOnboarding((s) => s.reach);
  // With the missions module the mission builds the prompt; show the part this page controls.
  const viaMissions = useOnboarding((s) => s.modules?.missions === true);
  const templates = useMemo(() => [...orderTemplates(raw), CUSTOM_TASK], [raw]);
  const projects = useProjects();
  const machines = useMachines();
  const machineMap = useOrgus((s) => s.machines);
  const checkoutMap = useOrgus((s) => s.checkouts);
  const checkouts = useMemo(() => Object.values(checkoutMap), [checkoutMap]);
  const own = thisMachine(machines, reach?.hostname);

  const [templateId, setTemplateId] = useState<string>('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [projectId, setProjectId] = useState('');
  const [checkoutId, setCheckoutId] = useState('');
  const [where, setWhere] = useState<'checkout' | 'worktree'>('worktree');
  const [branch, setBranch] = useState('');
  const [branchTouched, setBranchTouched] = useState(false);
  const [mode, setMode] = useState<'headless' | 'terminal'>('headless');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [result, setResult] = useState<StartResult | null>(null);

  const template = templates.find((t) => t.id === templateId) ?? null;

  // Defaults: the first template, the busiest project with an online checkout, its main checkout.
  useEffect(() => {
    if (!templateId && templates[0]) setTemplateId(templates[0].id);
  }, [templates, templateId]);
  useEffect(() => {
    if (projectId && projects.some((p) => p.id === projectId)) return;
    const usable = projects.find((p) => defaultTarget(p.id, checkouts, machineMap, own?.id)) ?? projects[0];
    if (usable) setProjectId(usable.id);
  }, [projects, projectId, checkouts, machineMap, own?.id]);
  useEffect(() => {
    if (!projectId) return;
    if (checkouts.some((c) => c.id === checkoutId && c.projectId === projectId && machineMap[c.machineId]?.online)) return;
    setCheckoutId(defaultTarget(projectId, checkouts, machineMap, own?.id)?.checkoutId ?? '');
  }, [projectId, checkoutId, checkouts, machineMap, own?.id]);
  useEffect(() => {
    if (!template) return;
    setValues({});
    setMode(template.mode);
    setWhere(template.branch ? 'worktree' : 'checkout');
    setBranchTouched(false);
    setTouched(false);
    setError(null);
    setResult(null);
  }, [template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const rendered = template ? renderTemplate(template, values) : null;
  const effectiveBranch = branchTouched ? branch : (rendered?.branch ?? '');
  const runOptions = checkouts
    .filter((c) => c.projectId === projectId && machineMap[c.machineId]?.online)
    .sort((a, b) => Number(a.kind === 'worktree') - Number(b.kind === 'worktree') || a.path.localeCompare(b.path))
    .map((c) => ({ value: c.id, label: `${machineMap[c.machineId]?.name ?? 'Machine'}: ${c.branch ?? 'detached'} in ${shortPath(c.path, 28)}${c.kind === 'worktree' ? ' (worktree)' : ''}` }));
  const checkout = checkoutMap[checkoutId];

  const valuesError = template?.id === CUSTOM_TASK.id && !values.description?.trim()
    ? 'A task description is needed. Enter a description to start the mission.'
    : template ? templateValuesError(template, values) : null;
  const branchError = where === 'worktree' && !safeBranch(effectiveBranch) ? 'Name the branch for the new worktree.' : null;
  const targetError = !checkout ? 'No online machine has this project checked out. Connect one, or pick another project.' : null;

  if (projects.length === 0) {
    return (
      <Callout
        title="A mission runs in a repo"
        action={
          <Button variant="primary" size="sm" icon={<FolderGit2 />} onClick={onConnectRepo}>
            Connect a repo
          </Button>
        }
      >
        Connect one in step 3, then come back: the templates below will be ready.
      </Callout>
    );
  }

  const start = async () => {
    setTouched(true);
    if (!template || !checkout || valuesError || branchError || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await startFromTemplate(template, {
        projectId,
        machineId: checkout.machineId,
        checkoutId: checkout.id,
        values,
        mode,
        newWorktree: where === 'worktree' ? { branch: safeBranch(effectiveBranch)! } : null,
      });
      setResult(r);
      toast.success(`Mission started on ${machineMap[checkout.machineId]?.name ?? 'the machine'}`, r.title);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="flex flex-col gap-4">
        <Callout
          tone="ok"
          title={`Started: ${result.title}`}
          action={
            <Button variant="primary" size="sm" iconRight={<ArrowRight />} onClick={() => navigate(result.href)}>
              {result.via === 'missions' ? 'Open the mission' : 'Watch it in Sessions'}
            </Button>
          }
        >
          {mode === 'terminal'
            ? 'A terminal window opened on the machine with Claude Code and the task. It shows up here within a few seconds.'
            : 'Claude Code is working in the background. Its session, tool calls and any question it asks show up here live.'}
          {result.missionId && <MissionLive missionId={result.missionId} machineId={result.machineId} />}
        </Callout>
        <div>
          <Button variant="ghost" size="sm" onClick={() => setResult(null)}>
            Start another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {phase === 'loading' && !templates.length ? (
        <div className="grid gap-2 sm:grid-cols-2" role="status" aria-label="Loading templates">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[72px]" />
          ))}
        </div>
      ) : phase === 'error' && !templates.length ? (
        <ErrorState error={loadError} onRetry={() => loadTemplates()} />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Mission templates">
          {templates.map((t) => (
            <TemplateCard key={t.id} t={t} selected={t.id === templateId} onSelect={() => setTemplateId(t.id)} />
          ))}
        </div>
      )}

      {template && rendered && (
        <div className="flex flex-col gap-4 rounded-card border border-border bg-surface p-4">
          <div>
            <p className="text-caption font-medium text-fg-muted">Goal</p>
            <p className="text-ui text-fg">{rendered.goal}</p>
          </div>

          {template.fields.map((f) =>
            f.multiline ? (
              <Textarea
                key={f.key}
                label={f.label}
                placeholder={f.placeholder}
                hint={f.hint}
                rows={3}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                error={touched && f.required && !(values[f.key] ?? '').trim() ? (template.id === CUSTOM_TASK.id ? valuesError ?? undefined : `Fill in ${f.label.toLowerCase()}.`) : undefined}
              />
            ) : (
              <Input
                key={f.key}
                label={f.label}
                placeholder={f.placeholder}
                hint={f.hint}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                error={touched && f.required && !(values[f.key] ?? '').trim() ? `Fill in ${f.label.toLowerCase()}.` : undefined}
              />
            ),
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Select label="Project" value={projectId} onValueChange={setProjectId} options={projects.map((p) => ({ value: p.id, label: p.name }))} />
            <Select
              label="Runs on"
              value={checkoutId}
              onValueChange={setCheckoutId}
              options={runOptions}
              placeholder={runOptions.length ? undefined : 'No online machine has it'}
              error={touched ? (targetError ?? undefined) : undefined}
            />
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-2">
              <span className="text-caption font-medium text-fg-muted">Where</span>
              <Segmented
                label="Where the agent works"
                value={where}
                onChange={setWhere}
                options={[
                  { value: 'worktree', label: 'New worktree', icon: <GitBranchPlus /> },
                  { value: 'checkout', label: 'In the checkout', icon: <FolderGit2 /> },
                ]}
              />
            </div>
            {where === 'worktree' && (
              <Input
                wrapperClassName="min-w-56 flex-1"
                label="Branch"
                value={effectiveBranch}
                onChange={(e) => {
                  setBranchTouched(true);
                  setBranch(e.target.value);
                }}
                error={touched ? (branchError ?? undefined) : undefined}
              />
            )}
            <div className="flex flex-col gap-2">
              <span className="text-caption font-medium text-fg-muted">How</span>
              <Segmented
                label="How the session runs"
                value={mode}
                onChange={setMode}
                options={[
                  { value: 'headless', label: 'In the background', icon: <MonitorPlay /> },
                  { value: 'terminal', label: 'In a terminal window', icon: <SquareTerminal /> },
                ]}
              />
            </div>
          </div>

          <details className="group rounded-control border border-border-subtle bg-bg">
            <summary className="cursor-pointer select-none px-3 py-2 text-caption text-fg-muted hover:text-fg">{viaMissions ? 'Instructions for the agent' : 'What the agent receives'}</summary>
            {viaMissions && (
              <p className="border-t border-border-subtle px-3 pt-2 text-caption text-fg-subtle">The mission adds its title and goal around these instructions when it starts the session.</p>
            )}
            <pre className={cn('max-h-56 overflow-auto whitespace-pre-wrap px-3 py-2 font-mono text-caption leading-relaxed text-fg-muted', !viaMissions && 'border-t border-border-subtle')}>
              {viaMissions ? rendered.instructions : templatePrompt(rendered)}
            </pre>
          </details>

          {error && <ErrorState error={error} onRetry={start} />}

          <div className="flex items-center gap-3">
            <Button variant="primary" icon={<Play />} loading={busy} onClick={start}>
              Start mission
            </Button>
            <span className="text-caption text-fg-subtle">
              {touched && (valuesError || targetError || branchError)
                ? (valuesError ?? targetError ?? branchError)
                : 'Runs Claude Code with your machine settings. You can stop it from Sessions at any time.'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
